import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import debounce from "lodash.debounce";
import { COUNTRY_OPTIONS, LEVEL_OPTIONS } from "../../constants";
import { getOptionName } from "../../utils/getOptionName";
import { prefetchUrl } from "../../utils/tabPrefetch";
import useCompactToolbar from "../../hooks/useCompactToolbar";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";

const PlayersDirectory = () => {
    const { t } = useTranslation();
    // this route has no admin-mode variant - it's always the public
    // directory - so it should always look anonymous to the backend, the
    // same way a signed-out visitor would. useAxios() would attach a
    // logged-in user's JWT unconditionally (see ClubsList.js), which risks
    // the backend scoping results down to just their own data instead of
    // every player.
    const api = axios;
    const history = useHistory();
    const location = useLocation();
    const { ref, inView } = useInView({ threshold: 1 });
    const searchInputRef = useRef(null);
    const { compactTable } = useTheme();
    const { toolbarSentinelRef, isCompact: isToolbarCompact, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        }
    });

    const [players, setPlayers] = useState([]);
    const [currentPage, setCurrentPage] = useState("");
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(10);

    // A Compact Table row (index.css .trow: 9px top/bottom padding + a 20px
    // avatar) runs ~40px tall, well under a normal card - the backend's own
    // default page size that comfortably fills one screen of cards left
    // several screens' worth of empty space below a compact list, forcing
    // extra scroll-triggered fetches just to reach a full viewport. Sizing
    // the first page off actual viewport height (plus a few extra rows so
    // the sentinel isn't right at the fold) fills the screen in one request
    // instead; card view keeps the backend default. See ClubsList.js's own
    // copy of this same formula.
    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    const playersSearch = async (query) => {
        setPlayers([]);
        setLoader(true);
        try {
            const hasQuery = query && query.length >= 2;
            // the no-query case is what a cold navigation to this tab
            // requests, and what SwipeTabsWrapper/BottomNav prefetch warms -
            // route it through the same shared cache so a swiped-to visit
            // can land on an already-settled response instead of waiting.
            // Compact mode's own `limit` makes this URL diverge from the
            // bare one BottomNav prefetches with (getDefaultTabUrl), so that
            // warm-cache hit is traded away here in exchange for filling the
            // screen in a single request instead of several.
            const res = hasQuery
                ? await api.get("/api/v1.0/players/", { params: { q: query, ...(compactTable ? { limit: compactPageSize() } : {}) } })
                : compactTable
                    ? await api.get("/api/v1.0/players/", { params: { limit: compactPageSize() } })
                    : await prefetchUrl(api, "/api/v1.0/players/");
            const results = res.data.results || [];
            const totalCount = res.data.count || 0;

            setPlayers(results);
            setCurrentPage(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const remaining = totalCount - results.length;
            setSkeletonCards(remaining > 0 ? Math.min(10, remaining) : 0);
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    };

    const debouncedSearch = useMemo(() => debounce(playersSearch, 400), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        document.title = t("Players");
        playersSearch();
    }, []);

    const getPlayers = async () => {
        setLoader(true);
        try {
            const response = await api.get(currentPage);
            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            setPlayers(prev => [...prev, ...results]);
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const updatedCount = players.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(10, remaining) : 0);
        } catch (error) {
            console.error(error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (inView && currentPage && !loader) {
            getPlayers();
        }
    }, [inView, currentPage, loader]);

    const handleItemClick = (player) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", player.id);
        history.push({ search: params.toString() });
    };

    return (
        <Container className="page_container">
            <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
            <div className={`sticky-toolbar ${isToolbarCompact ? "compact" : ""} ${!toolbarActivated ? "never-opened" : ""}`}>
                <nav className="tabs-container toolbar-search-row" style={{ paddingBottom: 0 }}>
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" />
                            <input
                                ref={searchInputRef}
                                className="search"
                                type="text"
                                placeholder={t("Players search: name, country, city...")}
                                onKeyUp={e => debouncedSearch(e.target.value)}
                                onBlur={e => { if (!e.target.value) collapseToolbar(); }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </li>
                    </ul>
                </nav>
            </div>

            {compactTable ? (
                <div className="trows">
                    {players.map((player, idx) => {
                        const subtitle = [
                            player.city,
                            t(getOptionName(COUNTRY_OPTIONS, player.country)),
                            player.level && t(getOptionName(LEVEL_OPTIONS, player.level))
                        ].filter(Boolean).join(", ");
                        return (
                            <CompactTableRow
                                key={player.id || idx}
                                variant="entity"
                                zebra={idx % 2 === 1}
                                avatarSrc={player.image}
                                title={<><span className="uppercase">{player.last_name}</span> {player.first_name}</>}
                                subtitle={subtitle}
                                onClick={() => handleItemClick(player)}
                            />
                        );
                    })}
                    {(loader || (currentPage && players.length === 0)) && skeletonCards > 0 && (
                        Array.from({ length: skeletonCards }).map((_, idx) => (
                            <div className="trow trow-entity" key={`skel-player-${idx}`} style={{ pointerEvents: "none" }}>
                                <Skeleton circle width={20} height={20} />
                                <Skeleton width="60%" height={12} />
                                <span />
                            </div>
                        ))
                    )}
                </div>
            ) : (
            <div className="entity-cards">
                {players.map((player, idx) => (
                    <div className="entity-card" key={player.id || idx} onClick={() => handleItemClick(player)}>
                        <div className="entity-card-top">
                            <div className="entity-card-thumb">
                                {player.image
                                    ? <img src={player.image} alt="" />
                                    : <FeatherIcon icon="user" />}
                            </div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title"><span className="uppercase">{player.last_name}</span> {player.first_name}</div>
                                <div className="entity-card-subtitle">
                                    {player.city}
                                    {player.city && player.country && ", "}
                                    {t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                    {(player.city || player.country) && player.level && ", "}
                                    {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {(loader || (currentPage && players.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                    <div className="entity-card" key={`skel-player-${idx}`} style={{ pointerEvents: "none" }}>
                        <div className="entity-card-top">
                            <div className="entity-card-thumb" style={{ background: "transparent" }}>
                                <Skeleton circle width={52} height={52} />
                            </div>
                            <div className="entity-card-titles">
                                <div className="entity-card-title"><Skeleton width="65%" height={15} /></div>
                                <div className="entity-card-subtitle"><Skeleton width="45%" height={12} style={{ marginTop: 4 }} /></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            )}
            <div ref={ref}></div>
        </Container>
    );
};

export default PlayersDirectory;
